import React from 'react';
import { motion } from 'framer-motion';
import { Cpu, Database, Globe2 } from 'lucide-react';

export const Ecosystem: React.FC = () => {
  return (
    <section id="ecosystem" className="py-32 px-6 relative z-10">
      {/* Ambient background connection glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-5xl h-[200px] bg-gradient-to-r from-transparent via-[#00ff9d]/5 to-transparent blur-[80px] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Header Area */}
        <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="inline-flex items-center gap-3 mb-6 px-4 py-2 rounded-full bg-white/[0.03] border border-green-500/20 backdrop-blur-md shadow-[0_0_15px_rgba(0,255,157,0.05)]">
              <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              <span className="text-[11px] uppercase tracking-[0.25em] font-medium text-green-300">
                Infrastructure
              </span>
            </div>
            
            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-white via-white to-white/60"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Infrastructure-grade<br className="hidden md:block" /> identity
            </h2>
          </motion.div>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="text-lg md:text-xl text-white/60 max-w-md font-light leading-relaxed"
          >
            AO verifies. Arweave stores. ARIO distributes. Together they make your passport durable and always accessible.
          </motion.p>
        </div>

        {/* Nodes Grid */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 relative">
          
          {/* Subtle connecting line behind the cards (desktop only) */}
          <div className="hidden md:block absolute top-[4.5rem] left-[10%] right-[10%] h-[1px] bg-gradient-to-r from-transparent via-green-500/20 to-transparent z-0" />

          {[
            {
              title: 'AO Network',
              subtitle: 'Hyper-parallel compute',
              description: 'Verifies activity at scale with real-time processing.',
              icon: Cpu,
              accent: 'rgba(59,255,122,0.8)' // Boosted opacity slightly for glows
            },
            {
              title: 'Arweave',
              subtitle: 'Permanent storage',
              description: 'Stores identity signals forever, independent of platforms.',
              icon: Database,
              accent: 'rgba(15,243,127,0.8)'
            },
            {
              title: 'ARIO',
              subtitle: 'Global gateway layer',
              description: 'Reliable access and resilient distribution worldwide.',
              icon: Globe2,
              accent: 'rgba(139,255,173,0.8)'
            }
          ].map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.7, delay: index * 0.15, type: "spring", bounce: 0.2 }}
                className="group relative bg-black/40 border border-white/10 rounded-[2rem] p-8 backdrop-blur-xl hover:border-white/20 transition-all duration-500 overflow-hidden z-10"
              >
                {/* Top glowing accent line */}
                <div 
                  className="absolute top-0 left-0 w-full h-[2px] opacity-40 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: `linear-gradient(90deg, transparent, ${item.accent}, transparent)` }} 
                />

                {/* Inner radial glow on hover */}
                <div 
                  className="absolute -top-24 -right-24 w-48 h-48 rounded-full blur-[60px] opacity-0 group-hover:opacity-20 transition-opacity duration-700 pointer-events-none"
                  style={{ background: item.accent }}
                />

                <div className="relative z-10 flex flex-col h-full">
                  <div className="flex items-center gap-5 mb-8">
                    {/* Hardware-styled Icon Box */}
                    <div 
                      className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3" 
                      style={{ 
                        background: item.accent,
                        boxShadow: `0 0 20px ${item.accent.replace('0.8', '0.2')}` 
                      }}
                    >
                      <Icon className="w-6 h-6 text-[#040606]" strokeWidth={2.5} />
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-bold text-white group-hover:text-green-50 transition-colors">
                        {item.title}
                      </h3>
                      <div className="text-[11px] uppercase tracking-[0.15em] font-mono text-green-400/70 mt-1">
                        {item.subtitle}
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-white/60 text-sm md:text-base leading-relaxed font-light mt-auto">
                    {item.description}
                  </p>

                  {/* Decorative data nodes */}
                  <div className="mt-8 flex gap-1.5 opacity-30 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="w-1 h-1 rounded-full bg-white/50" />
                    <div className="w-1 h-1 rounded-full bg-white/50" />
                    <div className="w-3 h-1 rounded-full bg-green-400" />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
        
      </div>
    </section>
  );
};
